import { B2bSavingPlanCategory, B2bSavingPlanType } from '@Common/types/b2b/plans';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import {
  B2bCompanyPlanTypeParameters,
  B2bManagedPlanRebalancingFrequency,
  B2bMatchingContributionComputedBy,
  B2bPlanEntryFeePaidBy,
  B2bPlanFeePaidBy,
} from '@Common/types/bo/b2b/plans';

export const b2bCompaniesSavingPlansTranslations = {
  button: 'Create savings plan',
  empty: 'This company has no plans yet',
  custom_cash_sources: {
    title: 'Cash sources per instrument for Gestion Libre only',
    subtitle:
      "The product's default allocation fund automatically includes all cash sources and it can't be modified.",
    instrument: 'Instrument',
    isin: 'ISIN',
    matching_contribution: 'Matching Contribution',
    all: 'ALL',
  },
  cash_sources: {
    voluntary_deposit: 'Voluntary deposits',
    participation: 'Profit-sharing bonus',
    profit_sharing: 'Incentive bonus',
    value_sharing_bonus: 'Value-sharing bonus',
    unused_vacation_days: 'Time savings account bonus',
    ccb_matured_election: 'Legal deadline',
  } satisfies Record<B2bCashSource, string>,
  sections: {
    general: {
      details: 'Details',
      default_instrument: 'Default funds',
      default_profile: 'Default profile',
      no_profile_available:
        'There is no investor profile defined for this company. Please create a profile first',
      custom_profile: 'Other profiles',
      custom_profile_add: 'Add profile',
      custom_profile_placeholder: 'Select a profile',
      instruments_universe: 'Instrument Universe',
      add_mutual_fund: 'Add mutual funds',
      minimum_tenure: 'Minimum tenure in months',
      minimum_tenure_reference_date: 'Reference date for tenure check',
      minimum_tenure_reference_date_help:
        'If set, tenure is checked at this date in the year preceding the investment',
      plan_label_help:
        'Optional. Tells apart two plans of the same type, which otherwise share the same name.',
      retirement_age_help:
        "Optional. Takes precedence over the company retirement age. Applied when an employee who hasn't set their own retirement age first invests in a PERECO plan, which confirms it.",
      name: 'Name',
      cash_sources: 'Cash sources to allow',
      matching_contributions: 'Matching contributions',
      matching_contributions_warning:
        'Please activate the matching contributions for the contract first before attempting to activate it for the product.',
      active: 'Active',
      fee_settings: 'Frequency Settings',
      is_cash_sources_per_instrument: 'Customisation',
      is_cash_sources_per_instrument_yes: 'Custom settings per funds',
      is_cash_sources_per_instrument_no: 'No customisation (apply to all funds)',
      is_cash_sources_per_instrument_discard_warning:
        'The settings per fund will be discarded, the plan will apply the same settings to all its funds.',
      is_cash_sources_per_instrument_switch_warning:
        'Choices already made are not affected; anything submitted after this change, including in campaigns in progress, uses the new settings.',
      matching_contribution_product_limit: 'Plan’s ceiling:',
      unilateral_matching_contribution: 'Unilateral matching contribution',
      unilateral_matching_contribution_limit: 'Unilateral matching contribution annual ceiling',
      unilateral_matching_contribution_limit_help:
        'Maximum amount per employee and per calendar year, up to 6,000 €',
      has_ccb: 'Does the Saving plan contain a CCB?',
      ccb_instruments_universe: 'Instrument Universe for CCB',
      ccb_parameters: 'CCB parameters',
      ccb_option_at_maturity: 'Option at maturity',
      ccb_payer_of_liquidity: 'Payer of the liquidity',
      ccb_handover_date: 'Handover date',
      add_ccb_instrument: 'Add CCB instrument',
      ccb_is_arbitrage_allowed_into_per: 'Is Arbitrage allowed into PER (PERCO/PERECO/PERO/PERU) ?',
      ccb_option_at_maturity_consultation: 'Consultation',
      ccb_option_at_maturity_final_withdrawal: 'Final withdrawal',
      ccb_option_at_maturity_final_arbitrage: 'Final arbitrage',
      ccb_payer_of_liquidity_company: 'Company',
      ccb_payer_of_liquidity_account_keeper: 'Account keeper',
    },
    matching_contributions: {
      general_settings: 'General parameters',
      configuration_rule: 'Configuration rule',
      matching_contribution_pool_type: 'Pool type',
      configuration_rule_simple: 'Simple Matching',
      configuration_csp_code: 'Per CSP code',
      configuration_csp_code_per_cash_source: 'Per CSP code per cash source',
      configuration_csp_code_per_cash_source_brackets: 'Per CSP code per cash source with brackets',
      configuration_rule_limit_brackets: 'Per Bracket',
      configuration_rule_limit_brackets_per_cash_source: 'Per Bracket per cash source',
      configuration_rule_seniority: 'Per Seniority',
      configuration_rule_company_simple: 'Simple Matching computed by the company',
      pool_type_cumulative_contributions: 'Cumulative contributions',
      pool_type_per_contribution: 'Per contribution',
      rule_settings: 'Rule settings',
      rule_settings_per_cash_source: 'Rule settings for %{cash_source}',
      effective_date: 'Effective date for matching contribution changes',
      effective_date_now: 'Apply the change now',
      effective_date_next: 'Apply on 01/01/%{year}',
      limit_unit: 'Limit unit',
      max_amounts: 'Max amounts',
      pass_limit: 'PASS for %{year}: ',
      pct_pass_limit: '%{percentage}% of PASS: ',
      simple: {
        source: 'Source to match',
        rate: 'Rate (%)',
        limit: 'Limit €',
        limit_pct: 'Limit % PASS',
      },
      csp_code: {
        add_row: 'Add a code',
        error_duplicated: 'This CSP code already exists on this plan.',
        no_code: 'No code',
        value: 'CSP code',
      },
      seniority_brackets: {
        tenure: 'Tenure in years',
        rate: 'Rate %',
        limit: 'Limit €',
        limit_pct: 'Limit % PASS',
      },
      limit_brackets: {
        rate: 'Rate (%)',
        from: 'From',
        to: 'To',
        max: 'Max',
        add_row: 'Add row',
        at_least_one: 'At least one cash source must be enabled',
      },
      matching_contribution_effect_modal: {
        title: 'Changes will not affect existing matching contribution orders',
        body: 'Updating the matching contribution configuration will only apply to new orders. Any existing matching contributions that are pending will not be retroactively affected by these changes.',
      },
      no_display_available: 'This matching contribution rule cannot be displayed.',
      no_save_available: 'This matching contribution rule cannot be saved.',
    },
  },
  actions: {
    create: {
      title: 'Create Savings Plan',
      success: 'Company Savings Plan created',
      failure: 'Company Savings Plan failed to be created: %{error}',
      steps: {
        '1': 'General Details',
        '2': 'CCB',
        '3': 'Matching Contributions',
        '4': 'Fees',
        '5': 'Custom Cash Sources',
      },
    },
    edit: {
      title: 'Edit Savings Plan',
      success: 'Company Savings Plan edited',
      failure: 'Company Savings Plan failed to be edited: %{error}',
      steps: {
        '1': 'General Details',
        '2': 'Matching Contributions',
        '3': 'Fees',
        '4': 'Effective Date',
      },
    },
    add_instrument: 'Add mutual fund',
  },
  fields: {
    id: 'Plan ID',
    supported_contribution_types: 'Cash sources to allow',
    social_contribution: 'Maximum authorised matching contributions ceiling',
    empty: 'There are no saving plans created yet',
    category: 'Type of Plan',
    name: 'Name',
    default_fund_instrument_id: 'Default funds',
    default_investor_profile: 'Default investor profile',
    other_investor_profiles: 'Other linked investor profiles',
    management_fee_pct: 'Management Fees % (in % of users AUM amount taken quarterly)',
    management_fee_paid_by: 'Paid by',
    management_fee_settings: 'Management Fees settings',
    management_fee_pct_input: 'Management Fees %',
    management_fee_paid_by_input: 'Paid by',
    rebalancing_frequency: 'Rebalancing Frequency',
    configuration_type: 'Configuration Type',
    matching_contribution_pool_type: 'Pool type',
    mc_enabled: 'Matching Contributions enabled',
    instruments_universe: 'Mutual funds universe',
    rate: 'Rate %',
    minimum_tenure: 'Minimum Tenure in months',
    minimum_tenure_reference_date: 'Reference date for tenure check (DD/MM)',
    matching_contribution_product_limit: 'Plan’s ceiling',
    unilateral_matching_contribution_limit: 'Unilateral matching contribution',
    cash_sources: 'Cash sources',
    matching_contribution_computed_by: 'Amounts computed by',
    type: 'Type',
    sub_type: 'Management Style',
    plan_label: 'Plan label',
    retirement_age: 'Default retirement age',
  },
  sub_type: {
    error: 'At least one management style should be selected',
  },
  errors: {
    plan_label_locked:
      'This company’s plan label is used to resolve default campaign allocations and cannot be changed until BRAV-10741 is resolved.',
  },
  creating_plan: 'Creating',
  management_fee_paid_by: {
    company: 'Company',
    employee: 'Affiliate',
  } satisfies Record<B2bPlanFeePaidBy, string>,
  entry_fee_paid_by: {
    company: 'Company',
    user: 'Affiliate',
  } satisfies Record<B2bPlanEntryFeePaidBy, string>,
  matching_contribution_table_title: 'Matching contribution settings',
  has_custom_matching_contribution_config:
    'Custom matching contribution settings, please contact administrator for more information',
  custody_fees_table: {
    title: 'Custody fees settings',
    employee_status: 'Applied to Employee with status',
    amount: 'Yearly amount (EUR)',
    paid_by: 'Paid by',
  },
  management_fees_table: {
    title: 'Management fees settings',
    employee_status: 'Applied to Employee with status',
    amount: 'Fees % (in % of users AUC amount taken quarterly)',
    paid_by: 'Paid by',
  },
  entry_fees_table: {
    title: 'Entry fees settings',
    employee_status: 'Applied to Employee with status',
    isin: 'ISIN',
    amount_pct: 'Total entry fees %',
    instrument: 'Instrument',
    legal_status: 'Legal status',
    paid_by: 'Paid by',
    fcpe_paid_by: 'Entry fees of FCPE will be paid by',
    employee_pct: 'Employee %',
    employee_pct_note:
      'Employee % is the part of the entry fee paid by the Employee, the counterpart will be paid by the Company.',
    amount_pct_company: 'Company %',
    amount_pct_employee: 'Employee %',
    entry_fee_split_note:
      'Company % and Employee % are both percentages of the order amount. Total entry fees % is calculated automatically as their sum.',
    cash_source: 'Cash source',
    cash_source_default: 'Default',
    cash_source_note:
      'Default fees apply to every cash source. Add a row to override a specific cash source.',
    add_cash_source_fee: 'Add a cash source fee',
    remove_cash_source_fee: 'Remove cash source fee',
  },
  max_tooltips: {
    minimum_tenure: '3 months',
    bracket_1_limit:
      'PEI: 8% of PASS ("Plafond Annuel de la Sécurité Sociale")\nPERECOI: 16% of PASS ("Plafond Annuel de la Sécurité Sociale")',
    bracket_1_contribution: '300%',
    bracket_2_limit:
      'PEI: 8% of PASS ("Plafond Annuel de la Sécurité Sociale")\nPERECOI: 16% of PASS ("Plafond Annuel de la Sécurité Sociale")',
    bracket_2_contribution: '300%',
  },
  types: {
    PEI_LIBRE: B2bCompanyPlanTypeParameters.PEI_LIBRE.name,
    PEI_PROFILE: B2bCompanyPlanTypeParameters.PEI_PROFILE.name,
    PEI: B2bCompanyPlanTypeParameters.PEI.name,
    PEE_LIBRE: B2bCompanyPlanTypeParameters.PEE_LIBRE.name,
    PEE_PROFILE: B2bCompanyPlanTypeParameters.PEE_PROFILE.name,
    PEE: B2bCompanyPlanTypeParameters.PEE.name,
    PEEG_LIBRE: B2bCompanyPlanTypeParameters.PEEG_LIBRE.name,
    PEEG_PROFILE: B2bCompanyPlanTypeParameters.PEEG_PROFILE.name,
    PEEG: B2bCompanyPlanTypeParameters.PEEG.name,
    PERECOI_LIBRE: B2bCompanyPlanTypeParameters.PERECOI_LIBRE.name,
    PERECOI_PILOTE: B2bCompanyPlanTypeParameters.PERECOI_PILOTE.name,
    PERECOI: B2bCompanyPlanTypeParameters.PERECOI.name,
    PERECO_LIBRE: B2bCompanyPlanTypeParameters.PERECO_LIBRE.name,
    PERECO_PILOTE: B2bCompanyPlanTypeParameters.PERECO_PILOTE.name,
    PERECO: B2bCompanyPlanTypeParameters.PERECO.name,
    PERECOG_LIBRE: B2bCompanyPlanTypeParameters.PERECOG_LIBRE.name,
    PERECOG_PILOTE: B2bCompanyPlanTypeParameters.PERECOG_PILOTE.name,
    PERECOG: B2bCompanyPlanTypeParameters.PERECOG.name,
    PERCOI_LIBRE: B2bCompanyPlanTypeParameters.PERCOI_LIBRE.name,
    PERCOI_PILOTE: B2bCompanyPlanTypeParameters.PERCOI_PILOTE.name,
    PERCOI: B2bCompanyPlanTypeParameters.PERCOI.name,
    PERCO_LIBRE: B2bCompanyPlanTypeParameters.PERCO_LIBRE.name,
    PERCO_PILOTE: B2bCompanyPlanTypeParameters.PERCO_PILOTE.name,
    PERCO: B2bCompanyPlanTypeParameters.PERCO.name,
    PERO_LIBRE: B2bCompanyPlanTypeParameters.PERO_LIBRE.name,
    PERO_PILOTE: B2bCompanyPlanTypeParameters.PERO_PILOTE.name,
    PERO: B2bCompanyPlanTypeParameters.PERO.name,
    PERU_LIBRE: B2bCompanyPlanTypeParameters.PERU_LIBRE.name,
    PERU_PILOTE: B2bCompanyPlanTypeParameters.PERU_PILOTE.name,
    PERU: B2bCompanyPlanTypeParameters.PERU.name,
    PER_LIBRE: B2bCompanyPlanTypeParameters.PER_LIBRE.name,
    PER_PILOTE: B2bCompanyPlanTypeParameters.PER_PILOTE.name,
    PER: B2bCompanyPlanTypeParameters.PER.name,
    ARTICLE_82_LIBRE: B2bCompanyPlanTypeParameters.ARTICLE_82_LIBRE.name,
    ARTICLE_82_PILOTE: B2bCompanyPlanTypeParameters.ARTICLE_82_PILOTE.name,
    ARTICLE_82: B2bCompanyPlanTypeParameters.ARTICLE_82.name,
    ARTICLE_83_LIBRE: B2bCompanyPlanTypeParameters.ARTICLE_83_LIBRE.name,
    ARTICLE_83_PILOTE: B2bCompanyPlanTypeParameters.ARTICLE_83_PILOTE.name,
    ARTICLE_83: B2bCompanyPlanTypeParameters.ARTICLE_83.name,
    PERECOER_LIBRE: B2bCompanyPlanTypeParameters.PERECOER_LIBRE.name,
    PERECOER_PILOTE: B2bCompanyPlanTypeParameters.PERECOER_PILOTE.name,
    PERECOER: B2bCompanyPlanTypeParameters.PERECOER.name,
    ER_EXTERNAL: B2bCompanyPlanTypeParameters.ER_EXTERNAL.name,
  } satisfies Record<B2bSavingPlanType | B2bSavingPlanCategory, string>,
  types_short: {
    PEI_LIBRE: 'PEI Libre',
    PEI_PROFILE: 'PEI Profilé',
    PEI: 'PEI',
    PEE_LIBRE: 'PEE Libre',
    PEE_PROFILE: 'PEE Profilé',
    PEE: 'PEE',
    PEEG_LIBRE: 'PEG Libre',
    PEEG_PROFILE: 'PEG Profilé',
    PEEG: 'PEG',
    PERECOI_LIBRE: 'PERECOI Libre',
    PERECOI_PILOTE: 'PERECOI Piloté',
    PERECOI: 'PERECOI',
    PERECO_LIBRE: 'PERECO Libre',
    PERECO_PILOTE: 'PERECO Piloté',
    PERECO: 'PERECO',
    PERECOG_LIBRE: 'PERECOG Libre',
    PERECOG_PILOTE: 'PERECOG Piloté',
    PERECOG: 'PERECOG',
    PERCOI_LIBRE: 'PERCOI Libre',
    PERCOI_PILOTE: 'PERCOI Piloté',
    PERCOI: 'PERCOI',
    PERCO_LIBRE: 'PERCO Libre',
    PERCO_PILOTE: 'PERCO Piloté',
    PERCO: 'PERCO',
    PERO_LIBRE: 'PERO Libre',
    PERO_PILOTE: 'PERO Piloté',
    PERO: 'PERO',
    PERU_LIBRE: 'PERU Libre',
    PERU_PILOTE: 'PERU Piloté',
    PERU: 'PERU',
    PER_LIBRE: 'PER Libre',
    PER_PILOTE: 'PER Piloté',
    PER: 'PER',
    ARTICLE_82_LIBRE: 'Article 82 Libre',
    ARTICLE_82_PILOTE: 'Article 82 Piloté',
    ARTICLE_82: 'Article 82',
    ARTICLE_83_LIBRE: 'Article 83 Libre',
    ARTICLE_83_PILOTE: 'Article 83 Piloté',
    ARTICLE_83: 'Article 83',
    PERECOER_LIBRE: 'PERECO ER Libre',
    PERECOER_PILOTE: 'PERECO ER Piloté',
    PERECOER: 'PERECO ER',
    ER_EXTERNAL: 'External ER',
  } satisfies Record<B2bSavingPlanType | B2bSavingPlanCategory, string>,
  rebalancing_frequency: {
    values: {
      '1_month': 'Every 1 month',
      '2_months': 'Every 2 months',
      '3_months': 'Every 3 months',
      '4_months': 'Every 4 months',
      '6_months': 'Every 6 months',
      '12_months': 'Every 12 months',
    } satisfies Record<B2bManagedPlanRebalancingFrequency, string>,
  },
  matching_contribution_computed_by: {
    distributor: 'Account keeper',
    company: 'Company',
  } satisfies Record<B2bMatchingContributionComputedBy, string>,
};
